import { Link } from 'react-router-dom';
import { AudioWaveform, Lock, Scissors, ShieldCheck, Sparkles, Wand, WifiOff, Film } from 'lucide-react';
import { brand, catalog } from '../engine/catalog';
import { AdSlot } from '../components/AdSlot';
import { categoryIcon, CATEGORY_VAR } from '../components/icons';

const BARS = [18, 34, 52, 40, 70, 92, 64, 80, 46, 30, 58, 88, 100, 74, 50, 36, 62, 84, 56, 38, 24, 44, 66, 48, 28];

export function HomePage() {
  const voiceCats = catalog.categories.filter((c) => c.kind !== 'tool');
  return (
    <div className="container">
      <section className="hero">
        <div>
          <span className="hero__eyebrow"><Sparkles size={14} /> Video voice editor</span>
          <h1>Make the voice in your videos sound exactly how you want.</h1>
          <p className="hero__lead">
            {brand.tagline} Reduce background noise, trim long pauses, review hesitations, and choose from{' '}
            {catalog.presets.length} voice effects — then export your video with the original picture untouched.
          </p>
          <div className="hero__cta">
            <Link to="/editor" className="btn btn--primary btn--lg"><Film size={18} /> Open a video</Link>
            <Link to="/effects" className="btn btn--lg">Browse effects</Link>
          </div>
          <p className="muted" style={{ marginTop: 16, fontSize: 14 }}>
            <Lock size={14} style={{ verticalAlign: -2 }} /> No upload, no account. Processing happens in your browser.
          </p>
        </div>
        <div className="hero__art" aria-hidden="true">
          <span className="badge badge--primary"><AudioWaveform size={12} /> Before → After</span>
          <div className="hero__bars">{BARS.map((h, i) => <span key={i} style={{ height: `${h}%` }} />)}</div>
        </div>
      </section>

      <section className="section">
        <div className="feature-grid">
          <Feature icon={<Wand size={20} />} title="Clean up speech">
            Spectral noise reduction, clarity EQ, de-essing, compression, breath and click reduction, and loudness normalization.
          </Feature>
          <Feature icon={<Scissors size={20} />} title="Pauses & hesitations">
            Find long pauses and possible “um/uh/hmm” sounds, review each one, and remove only what you choose. Everything is undoable.
          </Feature>
          <Feature icon={<Sparkles size={20} />} title={`${catalog.presets.length} voice effects`}>
            Robots, monsters, chipmunks, radio, cathedrals, studio polish — and a full Tapori character category.
          </Feature>
          <Feature icon={<ShieldCheck size={20} />} title="Video stays intact">
            When only audio changes, the original video stream is copied without re-encoding. Cuts re-time video frames to stay in sync.
          </Feature>
          <Feature icon={<WifiOff size={20} />} title="Private by design">
            Your video never leaves your device. No servers process your media.
          </Feature>
        </div>
      </section>

      <section className="section">
        <div className="section__head">
          <div>
            <h2>Effect categories</h2>
            <p className="muted" style={{ margin: 0 }}>Every effect is real audio processing — preview it on your own clip before exporting.</p>
          </div>
          <Link to="/effects" className="btn">See all effects</Link>
        </div>
        <div className="effect-grid">
          {voiceCats.map((c) => {
            const Icon = categoryIcon(c.id);
            const count = catalog.presets.filter((p) => p.category === c.id).length;
            return (
              <Link
                key={c.id}
                to={`/effects#${c.id}`}
                className="effect-card"
                style={{ ['--cat' as string]: CATEGORY_VAR(c.id), textDecoration: 'none', color: 'inherit' }}
              >
                <span className="effect-card__icon"><Icon size={18} /></span>
                <span className="effect-card__name">{c.name}</span>
                <span className="effect-card__desc">{count} presets · {c.description}</span>
              </Link>
            );
          })}
        </div>
      </section>

      <AdSlot slot="home" />
    </div>
  );
}

function Feature({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="card feature">
      <span className="feature__icon">{icon}</span>
      <h3 style={{ margin: 0 }}>{title}</h3>
      <p className="muted" style={{ margin: 0, fontSize: 14 }}>{children}</p>
    </div>
  );
}
